import { Center, Flex, Spinner } from '@chakra-ui/react';
import { useResizeObserver } from 'hooks/useResizeObserver';
import { ReactElement, memo, useRef, useState } from 'react';
import { FixedSizeGrid as Grid } from 'react-window';

import { GUTTER_SIZE, THUMBNAIL_HEIGHT, THUMBNAIL_WIDTH } from '../../InfiniteGrid/InfiniteGrid.config';
import { UploadImage } from '../UploadMenu.helper';
import { FolderImageCell } from './FolderImageCell';
import { IMAGE_COLUMNS } from './Folders.helper';

interface UserImagesProps {
  uploadImages: UploadImage[] | undefined;
  disableDragDrop?: boolean;
  setSelectedImages: (imageId: string) => void;
  selectedImages: string[];
}

/**
 * List of all user images
 */
export const FolderImages = memo(
  ({ uploadImages, disableDragDrop, setSelectedImages, selectedImages }: UserImagesProps): ReactElement => {
    const userImagesContainerRef = useRef<HTMLDivElement>(null);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

    // Update the infinite scroll box size when the window is resizing
    useResizeObserver({
      ref: userImagesContainerRef,
      onResize: ({ width = 0, height = 0 }) => {
        setContainerSize({ width, height });
      },
    });

    const uploadImagesData = {
      uploadImages,
      setSelectedImages,
      disableDragDrop,
      selectedImages,
    };

    return (
      <Flex
        ref={userImagesContainerRef}
        h='60vh'
        flexDir='column'
        data-testid='folder-images-container'
        overflowY='hidden'
      >
        {!!uploadImages ? (
          <>
            <Grid
              itemData={uploadImagesData}
              columnCount={IMAGE_COLUMNS}
              columnWidth={GUTTER_SIZE + THUMBNAIL_WIDTH}
              rowHeight={GUTTER_SIZE + THUMBNAIL_HEIGHT}
              height={containerSize.height}
              width={containerSize.width}
              rowCount={Math.ceil(uploadImages.length / IMAGE_COLUMNS)}
              data-testid='upload-folder-images-grid'
            >
              {FolderImageCell}
            </Grid>
          </>
        ) : (
          <Center>
            <Spinner data-testid='folder-upload-images-spinner' />
          </Center>
        )}
      </Flex>
    );
  },
);

FolderImages.displayName = 'FolderImages';
